import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { Container, Heading, Box, Button, SimpleGrid, Text, Table, Thead, Tr, Th } from '@chakra-ui/react';
import { supabase } from '../supabaseClient';
import { RaceStats } from '../types/types';
import { timeToSeconds } from '../utils/timeUtils';
import { TableHeader } from './shared/TableHeader';
import BackButton from './shared/BackButton';

interface RaceData {
  race_code: string;
  race_description: string;
  race_date: string;
  race_location: string;
  race_propositions: string;
}

interface RacerWithRaceData extends RaceStats {
  race_data?: RaceData;
}

interface SortConfig {
  key: keyof RacerWithRaceData | null;
  direction: 'asc' | 'desc' | null;
}

export default function CategoryView() {
  const { category, race_code } = useParams<{ category: string; race_code?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const raceCode = searchParams.get('race_code');
  const [racers, setRacers] = useState<RacerWithRaceData[]>([]);
  const [raceData, setRaceData] = useState<RaceData | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: null });

  // Add useEffect for scroll to top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSort = (key: keyof RacerWithRaceData) => {
    setSortConfig(current => {
      if (current.key !== key) {
        return { key, direction: 'asc' };
      }
      if (current.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      if (current.direction === 'desc') {
        return { key: null, direction: null };
      }
      return { key, direction: 'asc' };
    });
  };

  const normalizeTime = (timeStr: string): number => {
    if (!timeStr) return 999999;
    
    // Handle MM:SS.ms format (1:00.20)
    if (timeStr.includes(':') && timeStr.split(':').length === 2) {
      const [minutes, secondsPart] = timeStr.split(':');
      return (parseInt(minutes) * 60) + parseFloat(secondsPart);
    }
    
    // Handle HH:MM:SS.ms format (0:00:34.28)
    if (timeStr.includes(':') && timeStr.split(':').length === 3) {
      const [hours, minutes, secondsPart] = timeStr.split(':');
      return (parseInt(hours) * 3600) + (parseInt(minutes) * 60) + parseFloat(secondsPart);
    }
    
    // Handle SS.ms format (50.95)
    return parseFloat(timeStr);
  };

  const sortData = (data: RacerWithRaceData[]): RacerWithRaceData[] => {
    if (!sortConfig.key || !sortConfig.direction) {
      // Default sort by position ascending, with strings and zeros last
      return [...data].sort((a, b) => {
        const posA = a.position;
        const posB = b.position;

        // Handle null/undefined/empty values - put last
        if (!posA && !posB) return 0;
        if (!posA) return 1;
        if (!posB) return -1;

        // Handle "0" values - put last
        if (posA === "0" && posB === "0") return 0;
        if (posA === "0") return 1;
        if (posB === "0") return -1;

        // Handle string values (like "DNF") - put last
        const numA = parseInt(posA);
        const numB = parseInt(posB);
        if (isNaN(numA) && isNaN(numB)) return posA.localeCompare(posB);
        if (isNaN(numA)) return 1;
        if (isNaN(numB)) return -1;

        // Normal numeric comparison for valid numbers
        return numA - numB;
      });
    }

    return [...data].sort((a, b) => {
      const key = sortConfig.key!;
      const direction = sortConfig.direction!;

      // Handle null values
      const aValue = a[key];
      const bValue = b[key];
      
      if (aValue === null || aValue === undefined) {
        return direction === 'asc' ? 1 : -1;
      }
      if (bValue === null || bValue === undefined) {
        return direction === 'desc' ? 1 : -1;
      }

      // Special handling for time-based columns
      if (['total_time', 'first_run', 'second_run'].includes(key)) {
        const aTime = normalizeTime(String(aValue));
        const bTime = normalizeTime(String(bValue));
        return direction === 'asc' ? aTime - bTime : bTime - aTime;
      }

      // Handle position column
      if (key === 'position') {
        const numA = aValue === "0" ? Infinity : parseInt(String(aValue));
        const numB = bValue === "0" ? Infinity : parseInt(String(bValue));

        if (isNaN(numA) && isNaN(numB)) return String(aValue).localeCompare(String(bValue));
        if (isNaN(numA)) return 1;
        if (isNaN(numB)) return -1;

        return direction === 'asc' ? numA - numB : numB - numA;
      }

      // Handle year column
      if (key === 'year') {
        const yearA = parseInt(String(aValue)) || 0;
        const yearB = parseInt(String(bValue)) || 0;
        return direction === 'asc' ? yearA - yearB : yearB - yearA;
      }

      // Default string comparison for other columns (name, team)
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      return direction === 'asc' 
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });
  };

  const isValidUrl = (str: string) => {
    try {
      new URL(str);
      return true;
    } catch {
      return false;
    }
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
  };

  useEffect(() => {
    const fetchCategoryData = async () => {
      // Ensure we have valid route params before querying
      if (!category || !raceCode) {
        console.error('Missing category or raceCode for CategoryView query');
        return;
      }

      // Fetch race stats filtered by both category and race_code
      const { data: racersData, error: racersError } = await supabase
        .from('medvedica_race_stats')
        .select('*')
        .eq('race_category', category)
        .eq('race_code', raceCode);

      if (racersError) {
        console.error('Error fetching category data:', racersError);
        return;
      }

      let raceDetails: RaceData | null = null;

      // Then fetch race details for this specific race
      if (raceCode) {
        const { data: fetchedRaceDetails, error: raceError } = await supabase
          .from('medvedica_race_data')
          .select('*')
          .eq('race_code', raceCode)
          .single();

        if (raceError) {
          console.error('Error fetching race details:', raceError);
        } else {
          raceDetails = fetchedRaceDetails;
          setRaceData(fetchedRaceDetails);
        }
      }

      // Combine race stats with race details
      const racersWithRaceData: RacerWithRaceData[] = (racersData ?? []).map((racer: RaceStats) => ({
        ...(racer as RaceStats),
        // Ensure we pass undefined instead of null to match RacerWithRaceData type
        race_data: raceDetails || undefined
      }));

      setRacers(racersWithRaceData);
    };

    fetchCategoryData();
  }, [category, raceCode]);

  // Function to handle back button navigation
  const handleBack = () => {
    if (race_code) {
      navigate(`/race/${race_code}`);
    } else {
      navigate('/races/');
    }
  };

  return (
    <Container maxW="container.xl" py={8}>
      <BackButton 
        to={raceCode ? `/race/${raceCode}` : '/races/'} 
        label={raceCode ? "Back to race" : "Back to races"}
      />
      
      <Box px={4} pt={2}>
        <Heading mb={8}>Category: {category}</Heading>

        {/* Race Information Box */}
        {raceData && (
          <Box p={5} shadow="md" borderWidth="1px" borderRadius="md" mb={8}>
            <Heading size="md" mb={4}>Race Information</Heading>
            <SimpleGrid columns={[1, null, 2]} spacing={6}>
              <Box>
                <Text fontSize="lg" mb={2}>
                  <strong>Description:</strong>{' '}
                  <Text
                    as="span"
                    cursor="pointer"
                    color="inherit"
                    _hover={{ color: 'darkblue', textDecoration: 'underline' }}
                    onClick={() => navigate(`/race/${encodeURIComponent(raceData.race_code)}`)}
                  >
                    {raceData.race_description}
                  </Text>
                </Text>
                <Text fontSize="lg" mb={2}>
                  <strong>Date:</strong> {raceData.race_date ? formatDate(raceData.race_date) : ''}
                </Text>
                <Text fontSize="lg" mb={2}><strong>Location:</strong> {raceData.race_location}</Text>
                <Text fontSize="lg" mb={2}>
                  <strong>Propositions:</strong>{' '}
                  {isValidUrl(raceData.race_propositions) ? (
                    <Text
                      as="a"
                      href={raceData.race_propositions}
                      target="_blank"
                      rel="noopener noreferrer"
                      color="inherit"
                      _hover={{ color: 'darkblue', textDecoration: 'underline' }}
                      display="inline"
                      cursor="pointer"
                      /* Allow long URLs to break at any character on mobile */
                      wordBreak="break-all"
                      maxWidth={{ base: '100%', md: '500px' }}
                    >
                      {raceData.race_propositions}
                    </Text>
                  ) : (
                    raceData.race_propositions
                  )}
                </Text>
              </Box>
            </SimpleGrid>
          </Box>
        )}

        <Box overflowX="auto">
          {/* size="sm" on mobile for compact rows */}
          <Table variant="simple" size={{ base: 'sm', md: 'md' }}>
            <Thead bg="gray.50">
              <Tr>
                <TableHeader label="Position" sortKey="position" sortConfig={sortConfig} onSort={handleSort} />
                <TableHeader label="Racer Name" sortKey="name" sortConfig={sortConfig} onSort={handleSort} />
                <TableHeader label="Year" sortKey="year" sortConfig={sortConfig} onSort={handleSort} />
                <TableHeader label="Team" sortKey="team" sortConfig={sortConfig} onSort={handleSort} />
                <TableHeader label="Total Time" sortKey="total_time" sortConfig={sortConfig} onSort={handleSort} />
                <TableHeader label="First Run" sortKey="first_run" sortConfig={sortConfig} onSort={handleSort} />
                <TableHeader label="Second Run" sortKey="second_run" sortConfig={sortConfig} onSort={handleSort} />
              </Tr>
            </Thead>
            <tbody>
              {sortData(racers).map((racer) => (
                <tr key={racer.id}>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid #E2E8F0' }}>
                    {racer.position || 'DNF'}
                  </td>
                  <td 
                    style={{ 
                      padding: '8px 10px', 
                      borderBottom: '1px solid #E2E8F0', 
                      cursor: 'pointer',
                    }}
                    onClick={() => navigate(`/racer/${encodeURIComponent(racer.name)}`)}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'darkblue'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}
                  >
                    {racer.name}
                  </td>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid #E2E8F0' }}>
                    {racer.year || '-'}
                  </td>
                  {/* Team cell – truncated with ellipsis to prevent multi-line row inflation */}
                  <td 
                    style={{ 
                      padding: '8px 10px', 
                      borderBottom: '1px solid #E2E8F0', 
                      cursor: racer.team ? 'pointer' : 'default',
                      maxWidth: '120px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={racer.team || ''}
                    onClick={() => racer.team && navigate(`/teamDetail/${encodeURIComponent(racer.team)}`)}
                    onMouseEnter={(e) => racer.team && (e.currentTarget.style.color = 'darkblue')}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}
                  >
                    {racer.team || '-'}
                  </td>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid #E2E8F0' }}>
                    {racer.total_time || '-'}
                  </td>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid #E2E8F0' }}>
                    {racer.first_run || '-'}
                  </td>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid #E2E8F0' }}>
                    {racer.second_run || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Box>
      </Box>
    </Container>
  );
} 